import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, ArrowRight, Search, User } from 'lucide-react';
import { api, DfeGameStateUserLookupResult } from '@/lib/api';

export function AdminUserLookupPage() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DfeGameStateUserLookupResult | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    // Always look up — the admin entered a username.
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const user = await api.adminLookupGameStateUser(trimmed);
      setResult(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'User not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_dfe_game_state/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <h1 className="text-2xl font-semibold">User Lookup</h1>
        <p className="text-sm text-muted-foreground mt-1">Look up game states by username</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Find User</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex gap-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Username (e.g. devuser2)"
              className="flex-1 px-3 py-2 border rounded-md bg-background text-sm"
            />
            <Button type="submit" disabled={!query.trim() || loading}>
              <Search className="h-4 w-4 mr-1" />
              {loading ? 'Looking up...' : 'Look Up'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 py-4">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card
          className="hover:bg-muted/50 transition-colors cursor-pointer"
          onClick={() =>
            navigate(`/a/arche_dfe_game_state/admin/users/${encodeURIComponent(result.username)}`)
          }
        >
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">{result.username}</p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
